import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { Modals } from "src/modals";
import { confirmModalDataAtom } from "src/modals/ModalConfirm/store/atoms";
import { ConfirmModalProps } from "src/modals/ModalConfirm/store/types";

import { modalsAtom } from "./atoms";

export const MODAL_CLOSE_DURATION_MS = 300;

export type ModalCloseResult = "closed" | "cancelled";

interface ActiveCloseRecord {
  timeoutId?: ReturnType<typeof setTimeout>;
  postCommitTimeoutId?: ReturnType<typeof setTimeout>;
  didCommitClose: boolean;
  resolve: (result: ModalCloseResult) => void;
  promise: Promise<ModalCloseResult>;
}

const activeCloseTimers = new Map<Modals, ActiveCloseRecord>();

const resolveActiveClose = (modal: Modals, active: ActiveCloseRecord, result: ModalCloseResult) => {
  if (activeCloseTimers.get(modal) !== active) {
    return;
  }

  if (active.timeoutId !== undefined) {
    clearTimeout(active.timeoutId);
  }
  if (active.postCommitTimeoutId !== undefined) {
    clearTimeout(active.postCommitTimeoutId);
  }
  activeCloseTimers.delete(modal);
  if (typeof document !== "undefined") {
    document.querySelector(`[data-modal=${modal}]`)?.removeAttribute("data-close");
  }
  active.resolve(result);
};

export const cancelModalClose = (modal: Modals) => {
  const active = activeCloseTimers.get(modal);
  if (active) {
    resolveActiveClose(modal, active, active.didCommitClose ? "closed" : "cancelled");
  }
};

export const clearAllModalCloseTimers = () => {
  activeCloseTimers.forEach((active, modal) => {
    resolveActiveClose(modal, active, active.didCommitClose ? "closed" : "cancelled");
  });
};

export const useModals = () => {
  const setModals = useSetAtom(modalsAtom);
  const setConfirmModalData = useSetAtom(confirmModalDataAtom);

  const openModal = useCallback(
    (modal: Modals) => {
      cancelModalClose(modal);
      setModals((modals) => (modals.includes(modal) ? modals : [...modals, modal]));
    },
    [setModals],
  );

  const closeModal = useCallback(
    (modal: Modals) => {
      const existing = activeCloseTimers.get(modal);
      if (existing) {
        return existing.promise;
      }

      if (typeof document !== "undefined") {
        document.querySelector(`[data-modal=${modal}]`)?.setAttribute("data-close", "");
      }

      let resolve: (result: ModalCloseResult) => void = () => undefined;
      const promise = new Promise<ModalCloseResult>((resolvePromise) => {
        resolve = resolvePromise;
      });
      const record: ActiveCloseRecord = {
        didCommitClose: false,
        resolve,
        promise,
      };

      record.timeoutId = setTimeout(() => {
        if (activeCloseTimers.get(modal) !== record) {
          return;
        }

        if (typeof document === "undefined") {
          resolveActiveClose(modal, record, "cancelled");
          return;
        }

        record.didCommitClose = true;
        setModals((modals) => modals.filter((m) => m !== modal));
        document.querySelector(`[data-modal=${modal}]`)?.removeAttribute("data-close");
        document.querySelector(`[data-modal=${modal}]`)?.setAttribute("data-open", "");

        record.postCommitTimeoutId = setTimeout(() => {
          resolveActiveClose(modal, record, "closed");
        }, 0); // Wait for commit so reopening cannot race teardown.
      }, MODAL_CLOSE_DURATION_MS);

      activeCloseTimers.set(modal, record);
      return promise;
    },
    [setModals],
  );

  const openConfirmModal = useCallback(
    (data: ConfirmModalProps) => {
      setConfirmModalData(data);
      openModal("ModalConfirm");
    },
    [openModal, setConfirmModalData],
  );

  return { openModal, closeModal, openConfirmModal };
};
